import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router'

import { setEcommerceReferer } from '~/App/utils/api/ecommerceReferer'
import { DesignSystem } from '~/DesignSystem'
import { Toaster } from '~/DesignSystem/components/sonner'

import AuthProvider, { useAuth } from './Auth/AuthProvider'
import BugSnag from './BugSnag'
import CableProvider from './CableProvider'
import CableSubscriptionProvider from './CableSubscriptionProvider'
import useHost from './Host/hooks/useHost'
import Intl from './Intl'
import { NotFoundPage } from './pages/404'
import { ClientErrorPage } from './pages/500'
import { ChatPage } from './pages/chat'
import { PreviewPage } from './pages/preview'
import SessionChatThreadsProvider from './SessionChatThreadsProvider'
import useApp from './slice/useApp'
import { setWidgetMode } from './utils/api/widgetMode'

const queryClient = new QueryClient()

function PrivateRoute() {
  const { authorized } = useAuth()

  if (!authorized) return <NotFoundPage />

  return (
    <CableProvider>
      <CableSubscriptionProvider>
        <SessionChatThreadsProvider>
          <Outlet />
        </SessionChatThreadsProvider>
      </CableSubscriptionProvider>
    </CableProvider>
  )
}

const App = () => {
  const { setDisplayStatus: setDisplayStatus, setWidget, widget } = useApp()
  const setLoaded = useHost('SET_LOADED')

  useHost('SET_DISPLAY_STATUS', setDisplayStatus)
  useHost('SET_ECOMMERCE_REFERER', setEcommerceReferer)
  useHost('SET_WIDGET', data => {
    setWidget(data)
    setWidgetMode(data.attributes.mode)
  })

  useEffect(() => {
    setLoaded(true)
  }, [])

  return (
    <BugSnag FallbackComponent={ClientErrorPage}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Intl locale={widget?.attributes.locale}>
            <DesignSystem primary={widget?.attributes.primary_color || undefined}>
              <BrowserRouter>
                <Routes>
                  <Route path="/preview" Component={PreviewPage} />
                  <Route element={<PrivateRoute />}>
                    <Route path="/chat" Component={ChatPage} />
                  </Route>
                  <Route path="*" Component={NotFoundPage} />
                </Routes>
              </BrowserRouter>
              <Toaster />
            </DesignSystem>
          </Intl>
        </AuthProvider>
      </QueryClientProvider>
    </BugSnag>
  )
}

export default App
